/* eslint-disable camelcase */
/* eslint-disable security/detect-object-injection */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ReportObject, mergeTrackDataObject } from "../../components/ConversationalFlow/types";
import { DEFAULT_DOMAIN } from "../../constants/constant";

type InitialState = {
  loggedIn: boolean;
  outletLoading: boolean;
  showSuccessFeedback: boolean,
  showFeedbackBox: boolean,
  user_id: string;
  report_list: ReportObject[];
  activeChatIndex: number;
  activeContextId: number;
  activeChatId: string;
  currentChatId: string;
  isNewUser: boolean;
  isOldReport: boolean;
  isScrollBottom: boolean;
  isShowEmptySlice: boolean;
  isFetchingQuesResponse: boolean;
  selectedDomain: string;
  isSliderClosedICon: boolean;
  combineTrackData: mergeTrackDataObject[];
  false_count: number;
  call_track1: boolean,
  call_track2: boolean,
  call_track3: boolean,
  isClarificationReload: boolean;
  isCallTrack1Loading: boolean;
  isCallTrack3Loading: boolean;
  isDrawerPinned: boolean,
  isFullscreen: boolean,
  activeConversationHistory: any[],
  promptInput: string,
  similar_data: any,
  db_inputs: any,
  showInsightsOnUi: boolean,
  isDarkMode: boolean,
  activeChatDetails: any,
  name?: string,
  userType?: string,
  lastLoginTime?: string,
  userCreated?: string,
  lastPasswordUpdated?: string,
  lastAskedQuestion: string
};

const storedAuthData = JSON.parse(localStorage.getItem("auth_state") as string);
const storedSelectedDomain = localStorage.getItem("selected_domain") || "";

const initialState: InitialState = {
  loggedIn: false,
  outletLoading: false,
  showSuccessFeedback: false,
  showFeedbackBox: false,
  user_id: storedAuthData ? storedAuthData.userInfo.email : "",
  report_list: [],
  activeChatIndex: 0,
  activeContextId: 0,
  activeChatId: '',
  currentChatId: '',
  isNewUser: true,
  isOldReport: false,
  isScrollBottom: true,
  isShowEmptySlice: false,
  isFetchingQuesResponse: false,
  selectedDomain: storedSelectedDomain || DEFAULT_DOMAIN,
  isSliderClosedICon: true,
  combineTrackData: [],
  false_count: 0,
  call_track1: false,
  call_track2: false,
  call_track3: false,
  isClarificationReload: false,
  isCallTrack1Loading: false,
  isCallTrack3Loading: false,
  isDrawerPinned: false,
  isFullscreen: false,
  activeConversationHistory: [],
  promptInput: '',
  similar_data: {},
  db_inputs: {},
  showInsightsOnUi: true,
  isDarkMode: false,
  activeChatDetails: {},
  name: '',
  userType: '',
  lastLoginTime: '',
  userCreated: '',
  lastPasswordUpdated: '',
  lastAskedQuestion: ''
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<boolean>) {
      state.loggedIn = action.payload;
    },
    setOutletLoading(state, action: PayloadAction<boolean>) {
      state.outletLoading = action.payload;
    },
    setShowFeedbackSuccess(state, action: PayloadAction<boolean>) {
      state.showSuccessFeedback = action.payload;
    },
    setShowFeedbackBox(state, action: PayloadAction<boolean>) {
      state.showFeedbackBox = action.payload;
    },
    setShowInsightsOnUi(state, action: PayloadAction<boolean>) {
      state.showInsightsOnUi = action.payload;
    },
    setDarkMode(state, action: PayloadAction<boolean>) {
      state.isDarkMode = action.payload
    },
    setInitialUserID(state, action: PayloadAction<string>) {
      state.user_id = action.payload;
    },
    setActiveContextId(state, action: PayloadAction<number>) {
      state.activeContextId = action.payload;
    },
    setIsNewUser(state, action: PayloadAction<boolean>) {
      state.isNewUser = action.payload;
    },
    setIsOldReport(state, action: PayloadAction<boolean>) {
      state.isOldReport = action.payload;
    },
    setIsEmptySlice(state, action: PayloadAction<boolean>) {
      state.isShowEmptySlice = action.payload;
    },
    setIsFetchingQuesResponse(state, action: PayloadAction<boolean>) {
      state.isFetchingQuesResponse = action.payload;
    },
    setIsDrawerPinned(state, action: PayloadAction<boolean>) {
      state.isDrawerPinned = action.payload
    },

    setIsFullscreen(state, action: PayloadAction<boolean>) {
      state.isFullscreen = action.payload
    },

    setUserChatDetails(state, action: PayloadAction<any>) {
      const normalizedDomain = action.payload?.domain_name?.trim?.().toLowerCase().replace(/\s+/g, "_");
      state.selectedDomain = normalizedDomain || DEFAULT_DOMAIN;
      localStorage.setItem("selected_domain", state.selectedDomain);
      state.isScrollBottom = true;
    },

    setUserInfoDetails(state, action: PayloadAction<any>) {
      state.name = action.payload.name
      state.userType = action.payload.user_type
      state.lastLoginTime = action.payload.last_login_time
      state.userCreated = action.payload.user_created
      state.lastPasswordUpdated = action.payload.last_password_updated
    },

    setActiveChatId(state, action: PayloadAction<any>) {
      state.activeChatId = action.payload
    },

    setActiveChatDetails(state, action: PayloadAction<any>) {
      state.activeChatDetails = action.payload
    },

    setCurrentChatId(state, action: PayloadAction<any>) {
      state.currentChatId = action.payload
    },

    setSimilarData(state, action: PayloadAction<any>) {
      state.similar_data = action.payload
    },

    setDbInputData(state, action: PayloadAction<any>) {
      state.db_inputs = action.payload
    },

    updateConversationHistory(state, action: PayloadAction<any>) {
      state.activeConversationHistory = action.payload
    },

    setSelectedDomain(state, action: PayloadAction<string>) {
      state.selectedDomain = action.payload || DEFAULT_DOMAIN;
      localStorage.setItem("selected_domain", state.selectedDomain);
    },
    setIsSliderClosedICon(state, action: PayloadAction<boolean>) {
      state.isSliderClosedICon = action.payload;
    },

    setCombineTrackData(state, action: PayloadAction<mergeTrackDataObject[]>) {
      state.combineTrackData = action.payload;
    },

    setFalseCount(state, action: PayloadAction<number>) {
      state.false_count = action.payload;
    },

    setReloadClarification(state, action: PayloadAction<boolean>) {
      state.isClarificationReload = action.payload;
    },

    isCallTrack1Loading(state, action: PayloadAction<boolean>) {
      state.isCallTrack1Loading = action.payload;
    },

    isCallTrack3Loading(state, action: PayloadAction<boolean>) {
      state.isCallTrack3Loading = action.payload;
    },

    setCallTrack1(state, action: PayloadAction<boolean>) {
      state.call_track1 = action.payload;
    },

    setCallTrack2(state, action: PayloadAction<boolean>) {
      state.call_track2 = action.payload;
    },

    setCallTrack3(state, action: PayloadAction<boolean>) {
      state.call_track3 = action.payload;
    },

    setPromptInput(state, action: PayloadAction<string>) {
      state.promptInput = action.payload;
    },

    setLastAskedQuestion(state, action: PayloadAction<string>) {
      state.lastAskedQuestion = action.payload;
    }



  },
});


export const {
  setUser,
  setInitialUserID,
  setIsOldReport,
  setActiveContextId,
  setLastAskedQuestion,
  setIsNewUser,
  setUserChatDetails,
  setUserInfoDetails,
  setActiveChatId,
  setCurrentChatId,
  setOutletLoading,
  setShowFeedbackSuccess,
  setShowFeedbackBox,
  setIsFetchingQuesResponse,
  setSelectedDomain,
  setIsSliderClosedICon,
  setCombineTrackData,
  setFalseCount,
  setCallTrack1,
  setCallTrack2,
  setCallTrack3,
  isCallTrack1Loading,
  isCallTrack3Loading,
  setReloadClarification,
  setIsDrawerPinned,
  setIsFullscreen,
  updateConversationHistory,
  setPromptInput,
  setSimilarData,
  setDbInputData,
  setShowInsightsOnUi,
  setDarkMode,
  setActiveChatDetails
} = userSlice.actions;

export default userSlice.reducer;
